<template>
  <ul v-if="hasCounts" class="content-type-counts">
    <li
      class="content-type-count"
      v-for="item in filteredData"
      :key="item.contentType"
    >
      <ContentTypeIcon
        class="content-type-count-icon"
        :content-type="item.contentType"
      />
      <span class="content-type-count-text">{{ getTypeCountText(item) }}</span>
    </li>
  </ul>
</template>

<script>
import ContentTypeIcon from '@/components/ui/ContentTypeIcon'
import { formatNumber } from '@/utils/number'
import { ContentType } from '@/enums'

/**
 * Displays a list of content type counts.
 * If a count is 0, the content type isn't displayed.
 *
 * @author Thomas Grinderslev <lritgr@egmont.com>
 */
export default {
  name: 'ContentTypeCounts',
  components: { ContentTypeIcon },
  props: {
    /**
     * An array of content types and counts.
     * @type {Array<{count: number, contentType: ContentType}>}
     */
    data: {
      type: Array,
      required: true,
      validator: data =>
        !data.some(
          ({ count, contentType }) =>
            typeof count !== 'number' ||
            !Object.values(ContentType).includes(contentType)
        ),
    },
  },
  computed: {
    /**
     * Filters the given data to avoid rendering content types with counts lower than 1.
     * @returns {Array<{count: number, contentType: ContentType}>}
     */
    filteredData() {
      return this.data.filter(({ count }) => count > 0)
    },

    /**
     * @returns {boolean}
     */
    hasCounts() {
      return this.filteredData.length > 0
    },
  },
  methods: {
    /**
     * Returns the correct text count for the given type.
     *
     * @param {number} count
     * @param {ContentType} contentType
     * @returns {string}
     */
    getTypeCountText({ count, contentType }) {
      const formattedCount = formatNumber(count)

      switch (contentType) {
        case ContentType.ACTIVITY:
          return this.$t('content-counts.activity', { count, formattedCount })
        case ContentType.HOMEWORK:
          return this.$t('content-counts.homework', { count, formattedCount })
        case ContentType.LESSON:
          return this.$t('content-counts.lesson', { count, formattedCount })
        case ContentType.PDF_ASSIGNMENT:
          return this.$t('content-counts.pdf-assignment', {
            count,
            formattedCount,
          })
        case ContentType.SELF_TRAINING:
          return this.$t('content-counts.self-training', {
            count,
            formattedCount,
          })
        case ContentType.STANDARD_EXERCISE:
          return this.$t('content-counts.standard-exercise', {
            count,
            formattedCount,
          })
        case ContentType.TRIAL_EXAM:
          return this.$t('content-counts.trial-exam', {
            count,
            formattedCount,
          })
      }

      return formattedCount
    },
  },
}
</script>

<style lang="scss" scoped>
.content-type-counts {
  display: inline-block;
  padding: 0 var(--content-padding-small);
}

.content-type-count {
  display: inline-block;
  padding: 0.25em var(--content-padding-small);
  white-space: nowrap;

  // Override global Fessor style
  background-image: none;
}

.content-type-count-icon {
  $size: 1.5em;
  width: $size;
  height: $size;
  vertical-align: middle;
}

.content-type-count-text {
  font-size: 0.8em;
  text-transform: uppercase;
  padding-left: var(--content-padding-small);
}
</style>
